import React, { useState, useEffect, useRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Simple low-quality placeholder generator (creates a tiny colored placeholder)
const generatePlaceholder = (width = 10, height = 10, color = '#e0e0e0') => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Crect fill='${encodeURIComponent(color)}' width='${width}' height='${height}'/%3E%3C/svg%3E`;
};

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  wrapperClassName = '',
  placeholderSrc = null,
  threshold = 100,
  effect = 'blur',
  width,
  height,
  priority = false, // For above-the-fold images
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Generate a simple placeholder if none provided
  const placeholder = placeholderSrc || generatePlaceholder(width || 400, height || 300);

  // Preload priority images immediately
  useEffect(() => {
    if (priority && src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [priority, src]);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  // For priority images, render native img with loading="eager"
  if (priority) {
    return (
      <div className={`lazy-image-wrapper ${wrapperClassName} ${isLoaded ? 'loaded' : 'loading'}`}>
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className={`lazy-image ${className}`}
          loading="eager"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          width={width}
          height={height}
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          {...props}
        />
        {!isLoaded && (
          <div className="lazy-image-skeleton" aria-hidden="true">
            <div className="skeleton-shimmer"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`lazy-image-wrapper ${wrapperClassName} ${isLoaded ? 'loaded' : 'loading'} ${hasError ? 'error' : ''}`}>
      {!isLoaded && (
        <div className="lazy-image-skeleton" aria-hidden="true">
          <div className="skeleton-shimmer"></div>
        </div>
      )}
      <LazyLoadImage
        src={src}
        alt={alt}
        className={`lazy-image ${className}`}
        placeholderSrc={placeholder}
        effect={effect}
        threshold={threshold}
        afterLoad={handleLoad}
        onError={handleError}
        width={width}
        height={height}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
        wrapperProps={{
          style: { display: 'block', width: '100%', height: '100%' }
        }}
        {...props}
      />
    </div>
  );
};

export default LazyImage;
